import {
  SHBox,
  SHHtmlBlock,
  SHPopper,
  SHStack,
  SHTypography,
} from "@components/design-systems";
import { TooltipAllowSVG } from "@components/svgs";
import { WarningSVG } from "@components/svgs/warning";
import { IconButton, useTheme } from "@mui/material";
import { hexToRGBA } from "@utils";
import React, { useEffect, useRef, useState } from "react";
import { CloseButton } from "../close";

export interface WarningButtonProps {
  margin?: string | number;
  warningData?: {
    title: string | React.ReactNode;
    tooltip?: string | React.ReactNode;
  }[];
  customSVG?: React.ReactNode;
  maxWidth?: string;
}

export const WarningButtonV2 = ({
  margin,
  warningData,
  customSVG,
  maxWidth = "280px",
  ...others
}: WarningButtonProps) => {
  const { palette, shadows } = useTheme();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: any) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [wrapperRef, buttonRef, open]);

  return (
    <SHStack onClick={(e) => e.stopPropagation()}>
      <IconButton
        {...others}
        ref={buttonRef}
        onClick={handleClick}
        disableRipple
        sx={{
          width: "24px",
          height: "22px",
          borderRadius: "0",
          p: 0,
          "&:hover ,&:active": {
            opacity: 0.8,
          },
        }}
      >
        {customSVG ? (
          customSVG
        ) : (
          <WarningSVG
            backgroundColor={palette.warning.main}
            iconColor={palette.common.white}
          />
        )}
      </IconButton>

      <SHPopper
        anchorEl={anchorEl}
        open={open}
        placement="top-start"
        popperOptions={{ modifiers: [{ name: "flip", enabled: false }] }}
        sx={{ zIndex: 9999 }}
      >
        <SHBox pb={1}>
          <SHStack
            ref={wrapperRef}
            gap={1}
            sx={{
              p: 2,
              pr: 4,
              maxWidth: maxWidth,
              minHeight: "20px",
              bgcolor: hexToRGBA(palette.common.white, 0.9),
              border: "1px solid #E3E3E3",
              backdropFilter: "blur(5px)",
              borderRadius: "3px",
              boxShadow: shadows[1],
              position: "relative",
            }}
          >
            {warningData?.map((data, index) => {
              const titleComp = data.title;
              const contentComp = data.tooltip;

              if (!titleComp) return null;

              return (
                <SHStack key={index}>
                  {typeof titleComp === "string" ? (
                    <SHTypography variant="body1" zIndex="1">
                      {titleComp}
                    </SHTypography>
                  ) : (
                    titleComp
                  )}

                  {contentComp && typeof contentComp === "string" ? (
                    <SHHtmlBlock
                      variant="body3"
                      zIndex={1}
                      color={palette.text.third}
                      content={contentComp}
                    />
                  ) : (
                    contentComp && (
                      <SHBox
                        sx={{
                          fontSize: "13px",
                          fontWeight: 500,
                          color: palette.text.third,
                        }}
                      >
                        {contentComp}
                      </SHBox>
                    )
                  )}
                </SHStack>
              );
            })}
            <SHBox sx={{ position: "absolute", top: "10px", right: "10px" }}>
              <CloseButton onClick={handleClick} />
            </SHBox>
            <SHBox sx={{ position: "absolute", bottom: "-11.7px", left: 0 }}>
              <TooltipAllowSVG />
            </SHBox>
          </SHStack>
        </SHBox>
      </SHPopper>
    </SHStack>
  );
};
